import { useMemo } from "react";
import {
  BoltIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  HandRaisedIcon,
  InboxArrowDownIcon,
  InformationCircleIcon,
  MapPinIcon,
  PlayIcon,
  PresentationChartLineIcon,
  SquaresPlusIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Characteristic, Tooltip } from "@sablier/v2-components/molecules";
import { StreamCategory, StreamShape, links } from "@sablier/v2-constants";
import {
  useChainExplorer,
  useResolvedTypedAddress,
  useShortAddress,
} from "@sablier/v2-hooks";
import { useResolvedENS } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { policy } from "@sablier/v2-machines";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { useChain, useToken, useTokens, useWarning } from "~/client/hooks";
import type {
  ArrayItem,
  IAddress,
  IAutofillParams,
  IChainDisplay,
  ITiming,
  ITokenDisplay,
  IValue,
} from "@sablier/v2-types";
import type { ComponentProps } from "react";
import type { IExtensionDependencies } from "~/client/types";

type Description = ComponentProps<typeof Characteristic>["description"];

interface BatchProps {
  label: string;
  size?: number;
}

function Batch({ label, size }: BatchProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    const content = `${t("words.group")} #${label}`.concat(
      size ? ` (${size} ${_.capitalize(t("words.streams"))})` : "",
    );

    return [
      {
        purpose: "label",
        options: {
          icon: SquaresPlusIcon,
          value: t("characteristic.linkTo"),
        },
      },
      {
        purpose: "content",
        options: {
          value: content,
        },
      },
    ];
  }, [label, size, t]);

  return <Characteristic description={description} />;
}

interface SenderProps {
  chainId: number;
  address: string;
  proxy?: string;
}

function Sender({ address, chainId, proxy }: SenderProps) {
  const { t } = useT();
  const { data: ens } = useResolvedENS({
    address,
    chainId,
  });

  const shortAddress = useShortAddress(address);
  const shortProxy = useShortAddress(proxy);

  const description = useMemo((): Description => {
    const builder: Description = [];
    builder.push(
      {
        purpose: "label",
        options: {
          icon: UserIcon,
          value: `${_.capitalize(t("words.sender"))}:`,
        },
      },
      {
        purpose: "copy",
        options: {
          value: shortAddress,
          toCopy: address,
        },
      },
    );

    if (ens) {
      builder.push({
        purpose: "content",
        options: {
          value: `(${ens})`,
        },
      });
    }

    if (!_.isNilOrEmptyString(proxy)) {
      builder.push(
        {
          purpose: "content",
          options: {
            value: t("characteristic.throughProxy"),
          },
        },
        {
          purpose: "copy",
          options: {
            value: shortProxy,
            toCopy: proxy,
          },
        },
      );
    }

    builder.push({
      purpose: "external",
      options: {
        icon: InformationCircleIcon,
        to: links.docs.v2Proxy,
      },
    });

    return builder;
  }, [address, ens, proxy, shortAddress, shortProxy, t]);

  return <Characteristic description={description} />;
}

interface RecipientProps {
  chainId: number;
  address: string;
}

function Recipient({ address, chainId }: RecipientProps) {
  const { t } = useT();
  const { data: ens } = useResolvedENS({
    address,
    chainId,
  });

  const shortAddress = useShortAddress(address);

  const description = useMemo((): Description => {
    const builder: Description = [];
    builder.push(
      {
        purpose: "label",
        options: {
          icon: UserIcon,
          value: `${_.capitalize(t("words.recipient"))}:`,
        },
      },
      {
        purpose: "copy",
        options: {
          value: shortAddress,
          toCopy: address,
        },
      },
    );

    if (ens) {
      builder.push({
        purpose: "content",
        options: {
          value: `(${ens})`,
        },
      });
    }

    return builder;
  }, [address, ens, shortAddress, t]);

  return <Characteristic description={description} />;
}

interface ShapeProps {
  amount: string | undefined;
  category: StreamCategory | undefined;
  shape: StreamShape | undefined;
  token: ITokenDisplay | undefined;
}

function Shape({ amount, category, shape, token }: ShapeProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    const resolved = Stream.findShape(shape);
    const value = resolved.isUnknown ? category : t(resolved.title);

    return [
      {
        purpose: "label",
        options: {
          icon: PresentationChartLineIcon,
          value: `${t("words.shape")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: `${_.capitalize(value)} ${t("words.of")}`,
        },
      },
      {
        purpose: "amount",
        options: {
          token,
          value: amount,
          isFormatted: true,
        },
      },
    ];
  }, [amount, category, shape, token, t]);

  return <Characteristic description={description} />;
}

interface ChainProps {
  chain?: IChainDisplay;
}

function Chain({ chain }: ChainProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: GlobeAltIcon,
          value: `${t("words.chain")}:`,
        },
      },
      {
        purpose: "chain",
        options: {
          image: chain?.image,
          name: chain?.name,
        },
      },
    ];
  }, [chain, t]);

  return <Characteristic description={description} />;
}

interface CancelableProps {
  flag?: boolean;
}

function Cancelable({ flag = false }: CancelableProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: HandRaisedIcon,
          value: `${t("words.cancelability")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: flag
            ? t("characteristic.cancelable")
            : t("characteristic.nonCancelable"),
        },
      },
    ];
  }, [flag, t]);

  return <Characteristic description={description} />;
}

interface ContractProps {
  chainId: number | undefined;
  tokenId: string | number;
  address: string;
}

function Contract({ chainId, tokenId, address }: ContractProps) {
  const { t } = useT();

  const lockup = useChainExplorer({
    chainId,
    type: "address",
    hash: address,
  });

  const nft = useChainExplorer({
    chainId,
    type: "token",
    hash: address,
    tokenId,
  });

  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: DocumentCheckIcon,
          value: `${t("words.contract")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("characteristic.viewLockup"),
        },
      },
      {
        purpose: "external",
        options: {
          value: "contract",
          to: lockup,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("structs.orThe"),
        },
      },
      {
        purpose: "external",
        options: {
          value: "NFT",
          to: nft,
        },
      },
    ];
  }, [lockup, nft, t]);

  return <Characteristic description={description} />;
}

interface SummaryGroupProps {
  cancelability?: boolean;
  stream?: ArrayItem<IExtensionDependencies<"group">["streams"]>;
  timing?: ITiming;
}

function Summary({ cancelability, stream, timing }: SummaryGroupProps) {
  const { t } = useT();

  const description = useMemo((): Description => {
    const builder: Description = [];

    if (
      _.isNil(cancelability) ||
      _.isNilOrEmptyString(timing) ||
      _.isNil(stream)
    ) {
      return builder;
    }

    builder.push(
      {
        purpose: "content",
        options: {
          value: cancelability
            ? t("characteristic.renounceable")
            : t("characteristic.nonRenounceable"),
        },
      },
      {
        purpose: "content",
        options: {
          value: t("words.for"),
        },
      },
      {
        purpose: "copy",
        options: {
          value: _.toShortAddress(stream.address),
          toCopy: stream.address,
        },
      },
      ...(timing === "duration"
        ? [
            {
              purpose: "content" as const,
              options: {
                value: `${t("words.of")} ${
                  _.toDuration(stream.duration, "time-estimate")[0]
                }`,
              },
            },
            {
              purpose: "content" as const,
              options: {
                value: t("characteristic.startSoon"),
              },
            },
          ]
        : [
            {
              purpose: "content" as const,
              options: {
                value: `${t("words.from")} ${
                  _.toDuration(stream.start, "date")[0]
                }`,
              },
            },
            {
              purpose: "content" as const,
              options: {
                value: `${t("words.to")} ${
                  _.toDuration(stream.end, "date")[0]
                }`,
              },
            },
          ]),
      {
        purpose: "content",
        options: {
          value: `.`,
        },
      },
    );

    return builder;
  }, [cancelability, timing, stream, t]);

  return <Characteristic description={description} />;
}

interface TransactionProps {
  chainId?: number;
  hash?: string;
}

function Transaction({ chainId, hash }: TransactionProps) {
  const { t } = useT();
  const explorer = useChainExplorer({ chainId, type: "tx", hash });

  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: MapPinIcon,
          value: `${t("words.transaction")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("characteristic.view"),
        },
      },
      {
        purpose: "external",
        options: {
          value: t("characteristic.createTransaction"),
          to: explorer,
        },
      },
    ];
  }, [explorer, t]);

  return <Characteristic description={description} />;
}

interface GroupResultProps {
  amount: string;
  token?: ITokenDisplay;
  recipients?: number;
}

function GroupResult({ amount, token, recipients = 0 }: GroupResultProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "content",
        options: {
          value: t("words.streaming"),
        },
      },
      {
        purpose: "amount",
        options: {
          token,
          value: amount,
        },
      },
      {
        purpose: "content",
        options: {
          value: `${t("words.to")} ${recipients} ${t("words.recipients")}`,
        },
      },
    ];
  }, [amount, token, recipients, t]);

  return <Characteristic description={description} />;
}

interface ExpectedProps {
  chain?: IChainDisplay;
  owner?: string;
}

function Expected({ chain, owner }: ExpectedProps) {
  const { preview } = useResolvedTypedAddress(owner, chain?.chainId);
  const { t } = useT();

  const description = useMemo((): Description => {
    const list: Description = [
      {
        purpose: "label",
        options: {
          color: "gray200",
          icon: InboxArrowDownIcon,
          value: `${t("words.watching")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: _.capitalize(t("words.streams")),
        },
      },
      {
        purpose: "content",
        options: {
          value: `${t("structs.startedBy")} `,
        },
      },
      {
        purpose: "content",
        options: {
          value: `${preview} (${t("words.you")}) ${t("words.on")}`,
        },
      },
      {
        purpose: "chain",
        options: {
          image: chain?.image,
          name: chain?.name,
        },
      },
    ];

    return list;
  }, [chain, preview, t]);

  return <Characteristic description={description} />;
}

interface FilterProps {
  chain?: IChainDisplay;
  sender?: IAddress;
  recipient?: IAddress;
  ids?: number;
  found?: number;
}

function Filter({ sender, recipient, chain, ids, found = 0 }: FilterProps) {
  const { t } = useT();
  const s = useResolvedTypedAddress(sender, chain?.chainId);
  const r = useResolvedTypedAddress(recipient, chain?.chainId);

  const description = useMemo((): Description => {
    const list: Description = [
      {
        purpose: "content",
        options: {
          value: found
            ? t("characteristic.listingFirst", { count: found })
            : `${t("characteristic.listingResults")}`,
        },
      },
      {
        purpose: "chain",
        options: {
          image: chain?.image,
          name: chain?.name,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("words.streams"),
        },
      },
    ];

    if (!_.isNilOrEmptyString(s.address) && _.isEthereumAddress(s.address)) {
      list.push(
        {
          purpose: "content",
          options: {
            value: t("words.involving"),
          },
        },
        {
          purpose: "copy-address",
          options: {
            isIconShown: false,
            preview: s.preview,
            toCopy: s.address,
            value: s.address,
          },
        },
      );
    }

    if (!_.isNilOrEmptyString(r.address) && _.isEthereumAddress(r.address)) {
      if (!_.isNilOrEmptyString(s.address) && _.isEthereumAddress(s.address)) {
        list.push({
          purpose: "content",
          options: {
            value: t("words.and"),
          },
        });
      } else {
        list.push({
          purpose: "content",
          options: {
            value: t("words.involving"),
          },
        });
      }

      list.push({
        purpose: "copy-address",
        options: {
          isIconShown: false,
          preview: r.preview,
          toCopy: r.address,
          value: r.address,
        },
      });
    }

    if (!_.isNilOrEmptyString(ids) && ids > 0) {
      list.push({
        purpose: "content",
        options: {
          value: t("characteristic.matchingStreamIds", { count: ids }),
        },
      });
    }
    return list;
  }, [s, r, chain, found, ids, t]);

  return <Characteristic description={description} />;
}

interface IdentifierProps {
  id: string;
  category: StreamCategory;
}

function Identifier({ id, category }: IdentifierProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: PlayIcon,
          value: `${_.capitalize(t("words.id"))}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("characteristic.uniqueId"),
        },
      },
      {
        purpose: "copy",
        options: {
          value: id,
          toCopy: id,
        },
      },
      {
        purpose: "content",
        options: {
          value: ` (${_.startCase(category)})`,
        },
      },
    ];
  }, [category, id, t]);

  return <Characteristic description={description} />;
}

interface StatusProps {
  status: string | undefined;
}

function Status({ status }: StatusProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "label",
        options: {
          icon: BoltIcon,
          value: `${t("words.status")}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: _.capitalize(status),
        },
      },
    ];
  }, [status, t]);

  return <Characteristic description={description} />;
}

interface SimulatedProps {
  amount?: string;
  token?: ITokenDisplay;
}

function Simulated({ amount, token }: SimulatedProps) {
  const { t } = useT();
  const description = useMemo((): Description => {
    return [
      {
        purpose: "content",
        options: {
          value: t("characteristic.outOfTotal"),
        },
      },
      {
        purpose: "amount",
        options: {
          token,
          value: amount,
          isFormatted: true,
        },
      },
    ];
  }, [amount, token, t]);

  return <Characteristic description={description} />;
}

interface StreamProps {
  stream: Stream | undefined;
  purpose?: Parameters<typeof useWarning>["0"];
  warning: string | undefined;
  value?: IValue;
}

function Instance({
  stream,
  purpose = "cancel",
  warning: external,
  value,
}: StreamProps) {
  const { t } = useT();

  const warnings = useMemo(
    () => ({
      canceled: policy.stream.itemCanceled(t),
      chain: policy.stream.itemUnchained(t),
      connect: policy.stream.itemWhitelist(t),
      early: policy.stream.itemEarly(t),
      ended: policy.stream.itemEnded(t),
      whitelist: policy.stream.itemWhitelist(t),
    }),
    [t],
  );

  const from = stream?.sender;
  const to = stream?.recipient;

  const shortFrom = useShortAddress(from, 4, -3);
  const shortTo = useShortAddress(to, 4, -3);

  const token = useToken(stream?.token);

  const { state } = useWarning(purpose, stream);

  const warning = useMemo(
    () => _.get(warnings, state) || external,
    [external, state, warnings],
  );

  const description = useMemo((): Description => {
    const builder: Description = [];
    const isWarned = !_.isNilOrEmptyString(warning);

    const identifier = !_.isNilOrEmptyString(stream?.alias)
      ? _.toAlias(stream?.alias)
      : _.toShort(stream?.id, 8, -8);

    builder.push(
      {
        purpose: "label",
        options: {
          color: isWarned ? "yellow" : undefined,
          icon: isWarned ? ExclamationTriangleIcon : CheckCircleIcon,
          value: `${identifier}:`,
        },
      },
      {
        purpose: "content",
        options: {
          value: shortFrom,
        },
      },
      {
        purpose: "content",
        options: {
          value: `${t("words.to")} ${shortTo},`,
        },
      },
    );

    if (purpose === "withdraw") {
      builder.push(
        {
          purpose: "content",
          options: {
            value: t("words.withdraw"),
          },
        },
        {
          purpose: "amount",
          options: {
            token,
            value: _.toNumeralPrice(value?.humanized || "0"),
            isFormatted: true,
          },
        },
      );
    } else if (purpose === "cancel") {
      builder.push(
        {
          purpose: "content",
          options: {
            value: t("words.deposited"),
          },
        },
        {
          purpose: "amount",
          options: {
            token,
            value: _.toNumeralPrice(stream?.depositAmount.humanized),
            isFormatted: true,
          },
        },
      );
    }

    return builder;
  }, [shortFrom, shortTo, stream, token, warning, purpose, value, t]);

  if (!_.isNilOrEmptyString(warning)) {
    return (
      <Tooltip
        value={`Warning: ${warning}`}
        placement={"topLeft"}
        mouseEnterDelay={0.1}
        mouseLeaveDelay={0}
      >
        <div>
          <Characteristic description={description} />
        </div>
      </Tooltip>
    );
  }

  return <Characteristic description={description} />;
}

interface AutofillProps extends IAutofillParams {}

function Autofill({
  amount,
  cancelable,
  cliff,
  duration,
  end,
  recipient,
  shape,
  start,
  steps,
  timing,
  token,
  unlock,
}: AutofillProps) {
  const { t } = useT();
  const { find: findToken } = useTokens();
  const { chain } = useChain();

  const description = useMemo((): Description => {
    const builder: Description = [];

    const found = findToken({
      address: token?.address,
      chainId: token?.chainId,
    });

    builder.push(
      {
        purpose: "content",
        options: {
          value: `${_.capitalize(t(Stream.findShape(shape.id).title))}`,
        },
      },
      {
        purpose: "content",
        options: {
          value: t("words.on"),
        },
      },
      {
        purpose: "chain",
        options: {
          image: chain?.image,
          name: chain?.name,
        },
      },
    );

    if (!_.isNilOrEmptyString(token) && found) {
      builder.push(
        {
          purpose: "content",
          options: {
            value: t("words.with"),
          },
        },
        {
          purpose: "amount",
          options: {
            token: found,
            value: amount || "0",
            isFormatted: true,
          },
        },
        {
          purpose: "content",
          options: {
            value: `(${_.toShortAddress(token.address)})`,
          },
        },
      );
    }

    if (!_.isNilOrEmptyString(cancelable)) {
      builder.push({
        purpose: "content",
        options: {
          value: ([true, "true"].includes(cancelable)
            ? t("characteristic.renounceable")
            : t("characteristic.nonRenounceable")
          ).toLowerCase(),
        },
      });
    }

    if (!_.isNilOrEmptyString(recipient)) {
      builder.push(
        {
          purpose: "content",
          options: {
            value: t("words.for"),
          },
        },
        {
          purpose: "copy",
          options: {
            value: _.toShortAddressOrName(recipient),
            toCopy: recipient,
          },
        },
      );
    }

    if (timing === "duration") {
      if (!_.isNilOrEmptyString(duration)) {
        builder.push({
          purpose: "content" as const,
          options: {
            value: `${t("words.of")} ${
              _.toDuration(duration, "time-estimate")[0]
            }`,
          },
        });
      }

      if (!_.isNilOrEmptyString(cliff)) {
        builder.push({
          purpose: "content" as const,
          options: {
            value: `(${t("structs.cliffFor")} ${
              _.toDuration(cliff, "time-estimate")[0]
            })`,
          },
        });
      }
    } else if (timing === "range") {
      if (!_.isNilOrEmptyString(start)) {
        builder.push({
          purpose: "content" as const,
          options: {
            value: `${t("words.from")} ${_.toDuration(start, "date")[0]}`,
          },
        });
      }

      if (!_.isNilOrEmptyString(end)) {
        builder.push({
          purpose: "content" as const,
          options: {
            value: `${t("words.to")} ${_.toDuration(end, "date")[0]}`,
          },
        });
      }

      if (!_.isNilOrEmptyString(cliff)) {
        builder.push({
          purpose: "content" as const,
          options: {
            value: `(${t("structs.cliffEnds")} ${
              _.toDuration(cliff, "date")[0]
            })`,
          },
        });
      }
    }

    if (!_.isNilOrEmptyString(steps)) {
      builder.push({
        purpose: "content" as const,
        options: {
          value: `${t("words.with")} ${steps} ${t("words.steps")}`,
        },
      });
    }

    if (!_.isNilOrEmptyString(token) && found) {
      if (!_.isNilOrEmptyString(unlock)) {
        builder.push(
          {
            purpose: "content",
            options: {
              value: t("words.and"),
            },
          },
          {
            purpose: "amount",
            options: {
              token: found,
              value: unlock || "0",
              isFormatted: true,
            },
          },
          {
            purpose: "content",
            options: {
              value: t("structs.initiallyUnlocked"),
            },
          },
        );
      }
    }

    return builder;
  }, [
    amount,
    cancelable,
    chain,
    cliff,
    duration,
    end,
    findToken,
    recipient,
    shape,
    start,
    steps,
    t,
    timing,
    token,
    unlock,
  ]);

  return <Characteristic description={description} />;
}

const Characteristics = {
  Autofill,
  Batch,
  Cancelable,
  Chain,
  Contract,
  GroupResult,
  Expected,
  Filter,
  Identifier,
  Sender,
  Recipient,
  Instance,
  Shape,
  Simulated,
  Status,
  Summary,
  Transaction,
};

export default Characteristics;
